import { useState } from "react";
import { storyElement } from "@/lib/story-element";
import { Backpack, AlertTriangle, ShieldAlert, Skull, Shield, Zap, ArrowUp, Users, Building2, Swords, Archive, Warehouse, MapPin } from "lucide-react";
import { ItemCard, type ItemData } from "./ItemCard";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLocale } from "@/hooks/useLocale";

interface LocationStorage {
  location_id?: string;
  location_name: string;
  items: (string | ItemData)[];
  metadata?: Record<string, any>;
}

interface InventoryPanelProps {
  items: (string | ItemData)[];
  weightLimit?: number;
  encumbrancePercent?: number;
  entityImageMap?: Record<string, string>;
  effectiveWeightLimit?: number;
  carryBonuses?: { source: string; value: number; type?: "flat" | "percent" }[];
  activeModifiers?: { stat: string; value: number; source: string }[];
  // Multi-scope inventories — undefined means mechanic not enabled, [] means enabled but empty
  partyInventory?: (string | ItemData)[] | null;
  orgInventory?: (string | ItemData)[] | null;
  factionInventory?: (string | ItemData)[] | null;
  stashInventory?: (string | ItemData)[] | null;
  locationStorages?: LocationStorage[] | null;
  partyWeightLimit?: number;
  orgWeightLimit?: number;
  // Economy cross-reference
  currencyAmount?: number | null;
  currencyName?: string | null;
}

function normalizeItem(item: string | ItemData, entityImageMap?: Record<string, string>): ItemData {
  const base: ItemData = typeof item === "string" ? { name: item, rarity: "common" } : item;
  if (!base.image_url && entityImageMap) {
    const key = base.name.toLowerCase().trim();
    if (entityImageMap[key]) base.image_url = entityImageMap[key];
  }
  return base;
}

type EncumbranceTier = "normal" | "encumbered" | "heavy" | "overburdened";

function getEncumbranceTier(percent: number): EncumbranceTier {
  if (percent >= 150) return "overburdened";
  if (percent >= 125) return "heavy";
  if (percent >= 100) return "encumbered";
  return "normal";
}

interface ScopeTab {
  id: string;
  label: string;
  icon: typeof Backpack;
  items: (string | ItemData)[];
  weightLimit?: number;
}

function InventoryContent({ items: raw, weightLimit: displayLimit, encumbrancePercent: encPctProp, entityImageMap, carryBonuses, activeModifiers, showEquipment = true }: {
  items: (string | ItemData)[];
  weightLimit?: number;
  encumbrancePercent?: number;
  entityImageMap?: Record<string, string>;
  carryBonuses?: { source: string; value: number; type?: "flat" | "percent" }[];
  activeModifiers?: { stat: string; value: number; source: string }[];
  showEquipment?: boolean;
}) {
  const { t } = useLocale();

  const TIER_CONFIG: Record<EncumbranceTier, { label: string; icon: typeof AlertTriangle; color: string; barColor: string; desc: string }> = {
    normal: { label: t.play_weight, icon: Backpack, color: "text-muted-foreground", barColor: "bg-primary", desc: "" },
    encumbered: { label: t.play_encumbered, icon: AlertTriangle, color: "text-yellow-500", barColor: "bg-yellow-500", desc: "−2 DEX · Sluggish movement" },
    heavy: { label: t.play_heavy_load, icon: ShieldAlert, color: "text-orange-500", barColor: "bg-orange-500", desc: "−4 DEX · Cannot run" },
    overburdened: { label: t.play_overburdened, icon: Skull, color: "text-destructive", barColor: "bg-destructive", desc: "Cannot flee · Disadvantage on physical" },
  };

  const normalized = raw.map(i => normalizeItem(i, entityImageMap));
  const totalWeight = normalized.reduce((sum, i) => sum + ((i.weight || 0) * (i.quantity || 1)), 0);
  const loadPercent = encPctProp ?? (displayLimit ? Math.round((totalWeight / displayLimit) * 100) : 0);
  const tier = getEncumbranceTier(loadPercent);
  const tierCfg = TIER_CONFIG[tier];
  const TierIcon = tierCfg.icon;
  const barWidth = Math.min(100, loadPercent);

  const equippedItems = showEquipment ? normalized.filter(i => i.equipped) : [];
  const unequippedItems = showEquipment ? normalized.filter(i => !i.equipped) : normalized;

  const categories = new Map<string, ItemData[]>();
  for (const item of unequippedItems) {
    const cat = item.category || "misc";
    if (!categories.has(cat)) categories.set(cat, []);
    categories.get(cat)!.push(item);
  }

  const hasCarryBonuses = carryBonuses && carryBonuses.length > 0;
  const hasActiveModifiers = activeModifiers && activeModifiers.length > 0;

  if (normalized.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground/50">
        <Backpack className="w-8 h-8" />
        <p className="text-xs">{t.play_inventory_empty}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayLimit != null && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className={cn("flex items-center gap-1.5 font-medium", tierCfg.color)}>
              <TierIcon className="w-3.5 h-3.5" />
              {tier !== "normal" ? tierCfg.label : t.play_weight}
            </span>
            <span className={cn("font-bold tabular-nums", tier !== "normal" ? tierCfg.color : "text-foreground")}>
              {totalWeight}<span className="text-muted-foreground font-normal">/{displayLimit}</span>
              {loadPercent > 0 && (
                <span className={cn("ml-1 text-[10px] font-normal", tierCfg.color)}>({loadPercent}%)</span>
              )}
            </span>
          </div>
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 border border-border/30">
            <div className={cn("h-full transition-all duration-500 rounded-full", tierCfg.barColor)} style={{ width: `${barWidth}%` }} />
          </div>
          {hasCarryBonuses && (
            <div className="flex flex-wrap gap-1">
              {carryBonuses!.map((b, i) => (
                <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 h-4 font-normal text-green-400 border-green-500/20 bg-green-500/5">
                  <ArrowUp className="w-2.5 h-2.5 mr-0.5" />
                  +{b.value}{b.type === "percent" ? "%" : ""} {b.source}
                </Badge>
              ))}
            </div>
          )}
          {tier !== "normal" && (
            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 h-4 font-normal border-current/20", tierCfg.color)}>
              {tierCfg.desc}
            </Badge>
          )}
        </div>
      )}

      {hasActiveModifiers && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1">
            <Zap className="w-3 h-3" /> {t.play_active_bonuses}
          </div>
          <div className="flex flex-wrap gap-1">
            {activeModifiers!.map((m, i) => (
              <Badge key={i} variant="outline" className={cn("text-[9px] px-1.5 py-0 h-4 font-normal", m.value > 0 ? "text-green-400 border-green-500/20 bg-green-500/5" : "text-red-400 border-red-500/20 bg-red-500/5")}>
                {m.value > 0 ? "+" : ""}{m.value} {m.stat}
                <span className="text-muted-foreground ml-0.5">({m.source})</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {equippedItems.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1">
            <Shield className="w-3 h-3" /> {t.play_equipped} ({equippedItems.length})
          </div>
          <div className="grid grid-cols-1 gap-1">
            {equippedItems.map((item, i) => <ItemCard key={`eq-${item.name}-${i}`} item={item} />)}
          </div>
        </div>
      )}

      {[...categories.entries()].map(([cat, catItems]) => (
        <div key={cat} className="space-y-1">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest capitalize">
            {cat} ({catItems.length})
          </div>
          <div className="grid grid-cols-1 gap-1">
            {catItems.map((item, i) => <ItemCard key={`${item.name}-${i}`} item={item} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function InventoryPanel({
  items, weightLimit, encumbrancePercent, entityImageMap, effectiveWeightLimit, carryBonuses, activeModifiers,
  partyInventory, orgInventory, factionInventory, stashInventory, locationStorages, partyWeightLimit, orgWeightLimit,
  currencyAmount, currencyName,
}: InventoryPanelProps) {
  const scopes: ScopeTab[] = [
    { id: "player", label: "Personal", icon: Backpack, items, weightLimit: effectiveWeightLimit ?? weightLimit },
  ];

  // Show tabs if the scope is enabled (not undefined/null) — even when empty
  if (partyInventory != null) {
    scopes.push({ id: "party", label: "Party", icon: Users, items: partyInventory, weightLimit: partyWeightLimit });
  }
  if (orgInventory != null) {
    scopes.push({ id: "org", label: "Organization", icon: Building2, items: orgInventory, weightLimit: orgWeightLimit });
  }
  if (factionInventory != null) {
    scopes.push({ id: "faction", label: "Faction", icon: Swords, items: factionInventory });
  }
  if (stashInventory != null) {
    scopes.push({ id: "stash", label: "Stash", icon: Archive, items: stashInventory });
  }

  // Location storages — each becomes its own tab
  const locStorages = locationStorages ?? [];
  for (const loc of locStorages) {
    scopes.push({
      id: `loc_${loc.location_id || loc.location_name}`,
      label: loc.location_name,
      icon: MapPin,
      items: loc.items || [],
    });
  }

  const [activeScope, setActiveScope] = useState("player");
  const active = scopes.find(s => s.id === activeScope) || scopes[0];

  const showTabs = scopes.length > 1;

  return (
    <div {...storyElement("inventory-panel")} className="space-y-2">
      {currencyAmount != null && currencyAmount > 0 && (
        <div className="flex items-center justify-between px-2 py-1.5 rounded bg-muted/30 border border-border/30 mb-1">
          <span className="text-xs text-muted-foreground flex items-center gap-1">💰 {currencyName || "Gold"}</span>
          <span className="text-sm font-bold text-foreground">{currencyAmount}</span>
        </div>
      )}
      {showTabs && (
        <div className="flex flex-wrap gap-1 border-b border-border pb-1.5">
          {scopes.map(scope => {
            const Icon = scope.icon;
            const count = scope.items.length;
            const isActive = scope.id === active.id;
            return (
              <button
                key={scope.id}
                onClick={() => setActiveScope(scope.id)}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <Icon className="w-3 h-3" />
                {scope.label}
                <span className={cn("text-[9px] px-1 rounded-full", isActive ? "bg-primary/20" : "bg-muted")}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <InventoryContent
        items={active.items}
        weightLimit={active.weightLimit}
        encumbrancePercent={active.id === "player" ? encumbrancePercent : undefined}
        entityImageMap={entityImageMap}
        carryBonuses={active.id === "player" ? carryBonuses : undefined}
        activeModifiers={active.id === "player" ? activeModifiers : undefined}
        showEquipment={active.id === "player"}
      />
    </div>
  );
}